import { Component, ViewChild } from '@angular/core';
import { CouncilService } from '../shared/council.service';
import { Council } from '../shared/data/council.data';
import { ProgramGroupService } from '../shared/program-group.service';
import { ProgramGroup, ActivityType } from '../shared/data/program-group.data';
import { UntypedFormGroup, UntypedFormControl, Validators, NgForm, ValidatorFn, AbstractControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { map, startWith, filter } from 'rxjs/operators';
import { Activity, ActivityService } from '../shared/activity.service';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { GoogleAnalyticsService } from '../shared/google-analytics.service';

@Component({
  selector: 'app-enter-activity',
  templateUrl: './enter-activity.component.html',
  styleUrls: ['./enter-activity.component.css']
})
export class EnterActivityComponent {

  isSubmitting = false;
  viewLoaded = false;

  private subscriptions = new Subscription();

  constructor(private councilService: CouncilService,
              private programGroupService: ProgramGroupService,
              private activityService: ActivityService,
              private snackBar: MatSnackBar,
              private route: ActivatedRoute,
              private router: Router,
              private googleAnalyticsService: GoogleAnalyticsService) {

    this.membershipActivityForm = new UntypedFormGroup({
      council: new UntypedFormControl('', [Validators.required, this.councilValidator()]),
      submitterName: new UntypedFormControl('', [Validators.required]),
      submitterEmail: new UntypedFormControl('', [Validators.required, Validators.email]),
      submitterPhone: new UntypedFormControl('', [Validators.required]),
      activityType: new UntypedFormControl('', [Validators.required]),
      activityDate: new UntypedFormControl('', [Validators.required]),
      activityTitle: new UntypedFormControl('', [Validators.required]),
      activityDescription: new UntypedFormControl('', [Validators.required]),
    });

    this.nominationForm = new UntypedFormGroup({
      council: new UntypedFormControl('', [Validators.required, this.councilValidator()]),
      submitterName: new UntypedFormControl('', [Validators.required]),
      submitterEmail: new UntypedFormControl('', [Validators.required, Validators.email]),
      submitterPhone: new UntypedFormControl('', [Validators.required]),
      activityType: new UntypedFormControl('', [Validators.required]),
      activityDate: new UntypedFormControl('', [Validators.required]),
      activityTitle: new UntypedFormControl('', [Validators.required]),
      activityDescription: new UntypedFormControl('', [Validators.required]),
    });

    this.filteredCouncilsMembership = this.membershipActivityForm.get('council').valueChanges
      .pipe(
        startWith<string | Council>(''),
        map(value => typeof value === 'string' ? value : value.id),
        map(council => council ? this._filterCouncils(council) : this.getCouncils().slice())
      );

    this.filteredCouncilsNomination = this.nominationForm.get('council').valueChanges
      .pipe(
        startWith<string | Council>(''),
        map(value => typeof value === 'string' ? value : value.id),
        map(council => council ? this._filterCouncils(council) : this.getCouncils().slice())
      );

    this.subscriptions.add(this.route.queryParams.subscribe(params => {
      const councilRequested = this.councilService.getCouncilById(parseInt(params.council, 10));
      if(councilRequested) {
        this.membershipActivityForm.get('council').setValue(councilRequested);
        this.nominationForm.get('council').setValue(councilRequested);
      }
    }));
  }

  ngAfterViewInit(): void {
    // timeout required to avoid the dreaded 'ExpressionChangedAfterItHasBeenCheckedError'
    setTimeout(() => this.viewLoaded = true);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  selectedMembershipProgramGroup: ProgramGroup;
  selectedNominationProgramGroup: ProgramGroup;
  selectedMembershipActivity: ActivityType;
  selectedNominationType: ActivityType;
  membershipActivityForm: UntypedFormGroup
  nominationForm: UntypedFormGroup;
  filteredCouncilsMembership: Observable<Council[]>;
  filteredCouncilsNomination: Observable<Council[]>;
  minDate = new Date(2022, 6, 1);
  maxDate = new Date(2023, 5, 30);

  private _filterCouncils(value: string): Council[] {
    const filterValue = value.toLowerCase();
    return this.getCouncils()
      .filter(council => council.name.toLowerCase().indexOf(filterValue) >= 0
              || council.id.indexOf(filterValue) === 0
              || `${council.id} - ${council.name.toLowerCase()}`.indexOf(filterValue) === 0);
  }

  displayFn(council?: Council): string | undefined {
    return council ? `${council.id} - ${council.name}` : undefined;
  }

  getCouncils(): Council[] {
    return this.councilService.getCouncils();
  }

  getMembershipActivities(): ActivityType[] {
    let membershipActivities: ActivityType[] = [];
    this.programGroupService.getActivityTypes().forEach(activity => {
      if(activity.programGroupId === 'membership') {
        membershipActivities.push(activity);
      }
    });
    return membershipActivities;
  }

  getNonMembershipMandatoryActivities(): ActivityType[] {
    let mandatoryActivities: ActivityType[] = [];
    this.programGroupService.getActivityTypes().forEach(activity => {
      if(activity.id.indexOf('mandatory') >= 0 && activity.programGroupId !== 'membership') {
        mandatoryActivities.push(activity);
      }
    });
    return mandatoryActivities;
  }

  membershipActivitySelected() {
    this.selectedMembershipProgramGroup = this.programGroupService.getProgramGroupById(this.selectedMembershipActivity.programGroupId);
  }

  nominationTypeSelected() {
    this.selectedNominationProgramGroup = this.programGroupService.getProgramGroupById(this.selectedNominationType.programGroupId);
  }

  // TODO: Consider splitting this out
  councilValidator(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      if (control.value.id) {
        return null;
      } else {
        return {council: 'Please select a council'};
      }
    };
  }

  onSubmitMembershipActivity() {
    this.isSubmitting = true;

    const activity: Activity = {
      councilId: Number(this.membershipActivityForm.get('council').value.id),
      submitterName: this.membershipActivityForm.get('submitterName').value,
      submitterEmail: this.membershipActivityForm.get('submitterEmail').value,
      submitterPhone: this.membershipActivityForm.get('submitterPhone').value,
      programGroupId: this.selectedMembershipActivity.programGroupId,
      activityTypeId: this.membershipActivityForm.get('activityType').value.id,
      activityDate: this.membershipActivityForm.get('activityDate').value.toLocaleDateString('en-US', { timeZone: 'America/New_York' }),
      activityTitle: this.membershipActivityForm.get('activityTitle').value,
      activityDescription: this.membershipActivityForm.get('activityDescription').value,
    };

    this.submitActivity(activity);
  }
  
  onSubmitNomination() {
    this.isSubmitting = true;

    const activity: Activity = {
      councilId: Number(this.nominationForm.get('council').value.id),
      submitterName: this.nominationForm.get('submitterName').value,
      submitterEmail: this.nominationForm.get('submitterEmail').value,
      submitterPhone: this.nominationForm.get('submitterPhone').value,
      programGroupId: this.selectedNominationType.programGroupId,
      activityTypeId: this.nominationForm.get('activityType').value.id,
      activityDate: this.nominationForm.get('activityDate').value.toLocaleDateString('en-US', { timeZone: 'America/New_York' }),
      activityTitle: this.nominationForm.get('activityTitle').value,
      activityDescription: this.nominationForm.get('activityDescription').value,
    };
    
    this.submitActivity(activity);
  }

  private submitActivity(activity: Activity) {
    this.activityService.addActivity(activity).then(res => {
      console.log('added activity!');
      this.googleAnalyticsService.eventEmitter('activity_created', 'Activity Created');
      this.router.navigate(['council-summary', activity.councilId]);
    }).catch(err => {
      this.snackBar.open('Error submitting activity', 'Dismiss', {duration: 5000});
      this.googleAnalyticsService.eventEmitter('error_creating_activity', 'Error Creating Activity', 'exception');
      console.log('Error:', err.message);

      this.isSubmitting = false;
    });
  }

  openSupremeSite() {
    this.googleAnalyticsService.eventEmitter('open_supreme_10784_site', 'Open Supreme 10784 Site');
    window.open('https://kofc.org/un/en/forms/spa/invite.html?lang=en&form=10784C.01', '_blank');
  }
}
